import React, { useState, useEffect, useMemo, useRef } from "react";
import { bElEdProgrammeApi } from "../../config/api";
import { apiClient } from "../../utils/apiClient";
import "./BElEdProgramme.scss";

const SPECIALIZATIONS = [
  "Language & Literacy",
  "Mathematics Education",
  "Science Education",
  "Social Studies Education",
  "Arts & Craft Education",
  "Physical & Health Education",
];

const DURATIONS = ["2 Years", "4 Years"];
const MODES     = ["Full Time", "Part Time", "Distance"];

const SUBJECT_COLORS = {
  "Language & Literacy":        "#8b5cf6",
  "Mathematics Education":      "#0ea5e9",
  "Science Education":          "#10b981",
  "Social Studies Education":   "#f59e0b",
  "Arts & Craft Education":     "#ec4899",
  "Physical & Health Education":"#6366f1",
};

const DEFAULT_COLOR = "#64748b";

const mkProgrammeSubject = () => ({
  id: Date.now() + Math.random(),
  Subject_Name: "",
  Total_Seats: "",
  Group: ""
});

const initialProgrammes = [
  {
    id: 1,
    name: "B.El.Ed — Language & Literacy",
    code: "BELED-LL",
    specialization: "Language & Literacy",
    duration: "4 Years",
    semesters: 8,
    totalSeats: 50,
    annualFees: "₹28,000",
    mode: "Full Time",
    eligibility: "10+2 from a recognized board with minimum 50% marks. Candidates from any stream are eligible.",
    minQualification: "10+2 (Any Stream)",
    description: "A 4-year integrated undergraduate programme preparing elementary school teachers with a focus on language development, literacy pedagogy, and communication skills for Classes I–VIII.",
    affiliation: "Delhi University / State Board",
    accreditation: "NCTE Approved",
    practicum: "2 Semesters of School Internship",
    highlights: "School Internship, Language Lab, Literacy Workshops, Child Psychology",
    status: "published",
  },
  {
    id: 2,
    name: "B.El.Ed — Mathematics Education",
    code: "BELED-ME",
    specialization: "Mathematics Education",
    duration: "4 Years",
    semesters: 8,
    totalSeats: 50,
    annualFees: "₹28,000",
    mode: "Full Time",
    eligibility: "10+2 with Mathematics as a subject, minimum 50% marks from a recognized board.",
    minQualification: "10+2 with Mathematics",
    description: "An integrated teacher education programme focusing on mathematical thinking, problem solving pedagogy, and concept-based learning strategies for elementary school classrooms.",
    affiliation: "Karnataka State University",
    accreditation: "NCTE Approved",
    practicum: "2 Semesters of School Internship",
    highlights: "Maths Lab, Problem-Based Learning, School Practice, Peer Teaching",
    status: "published",
  },
  {
    id: 3,
    name: "B.El.Ed — Science Education",
    code: "BELED-SE",
    specialization: "Science Education",
    duration: "4 Years",
    semesters: 8,
    totalSeats: 50,
    annualFees: "₹28,000",
    mode: "Full Time",
    eligibility: "10+2 with Science stream, minimum 50% marks from a recognized board.",
    minQualification: "10+2 (Science)",
    description: "A comprehensive programme equipping future elementary teachers with science content knowledge, inquiry-based teaching methods, and hands-on laboratory skills for Classes I–VIII.",
    affiliation: "Karnataka State University",
    accreditation: "NCTE Approved",
    practicum: "2 Semesters of School Internship",
    highlights: "Science Lab, Inquiry Learning, Environmental Studies, Field Trips",
    status: "published",
  },
  {
    id: 4,
    name: "B.El.Ed — Social Studies Education",
    code: "BELED-SS",
    specialization: "Social Studies Education",
    duration: "4 Years",
    semesters: 8,
    totalSeats: 50,
    annualFees: "₹28,000",
    mode: "Full Time",
    eligibility: "10+2 from any stream with minimum 50% marks from a recognized board.",
    minQualification: "10+2 (Any Stream)",
    description: "A programme developing pedagogical skills in History, Geography, Civics, and Economics for elementary classroom teaching with emphasis on active and critical thinking.",
    affiliation: "Karnataka State University",
    accreditation: "NCTE Approved",
    practicum: "2 Semesters of School Internship",
    highlights: "Community Projects, Field Research, Maps & Charts Lab, Civic Education",
    status: "draft",
  },
];

const defaultForm = {
  _id: null,
  Programme_Name: "",
  Programme_Code: "",
  Specialization_Area: "",
  Duration: "4 Years",
  Total_Semesters: "",
  Total_Seats: "",
  Annual_Fees: "",
  Mode_of_Study: "Full Time",
  Full_Eligibility_Criteria: "",
  Minimum_Qualification_Required: "",
  Programme_Description: "",
  Affiliation: "",
  Accreditation_Approval: "NCTE Approved",
  Practicum_Internship_Details: "",
  Programme_Highlights: "",
  Programme_Subjects: [],
  Status: "Published",
};

const getSpecColor = (spec) => SUBJECT_COLORS[spec] || DEFAULT_COLOR;
const TABS = ["details", "academic", "eligibility", "settings"];

const BElEdProgramme = () => {
  const [programmes, setProgrammes]         = useState([]);
  const [loading, setLoading]               = useState(false);
  const [modalType, setModalType]           = useState("");
  const [formData, setFormData]             = useState(defaultForm);
  const [selectedItem, setSelectedItem]     = useState(null);
  const [confirmId, setConfirmId]           = useState(null);
  const [confirmAction, setConfirmAction]   = useState("");
  const [dropdownOpenId, setDropdownOpenId] = useState(null);
  const [dropdownPos, setDropdownPos]       = useState({ top: 0, left: 0 });
  const [filterSpec, setFilterSpec]         = useState("all");
  const [filterStatus, setFilterStatus]     = useState("all");
  const [filterDuration, setFilterDuration] = useState("all");
  const [searchText, setSearchText]         = useState("");
  const [activeTab, setActiveTab]           = useState("details");
  const dropdownRef = useRef(null);

  useEffect(() => { fetchProgrammes(); }, []);

  const fetchProgrammes = async () => {
    try {
      setLoading(true);
      const response = await apiClient(bElEdProgrammeApi);
      const result = await response.json();
      if (response.ok) { setProgrammes(result.data || []); }
    } catch (error) {
      console.error("Error fetching B.El.Ed programmes:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const onOut   = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpenId(null); };
    const onClose = ()  => setDropdownOpenId(null);
    document.addEventListener("mousedown", onOut);
    window.addEventListener("scroll", onClose, true);
    window.addEventListener("resize", onClose);
    return () => {
      document.removeEventListener("mousedown", onOut);
      window.removeEventListener("scroll", onClose, true);
      window.removeEventListener("resize", onClose);
    };
  }, []);

  const filtered = useMemo(() => {
    return programmes.filter((p) => {
      const matchSpec     = filterSpec     === "all" || p.Specialization_Area === filterSpec;
      const matchStatus   = filterStatus   === "all" || p.Status              === filterStatus;
      const matchDuration = filterDuration === "all" || p.Duration            === filterDuration;
      const matchSearch   =
        (p.Programme_Name || "").toLowerCase().includes(searchText.toLowerCase()) ||
        (p.Programme_Code || "").toLowerCase().includes(searchText.toLowerCase()) ||
        (p.Specialization_Area || "").toLowerCase().includes(searchText.toLowerCase());
      return matchSpec && matchStatus && matchDuration && matchSearch;
    });
  }, [programmes, filterSpec, filterStatus, filterDuration, searchText]);

  const handleDotsClick = (e, id) => {
    e.stopPropagation();
    if (dropdownOpenId === id) { setDropdownOpenId(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const mW = 205, mH = 170, gap = 8;
    let left = rect.right - mW;
    let top  = rect.bottom + gap;
    if (left < 12) left = 12;
    if (left + mW > window.innerWidth  - 12) left = window.innerWidth  - mW - 12;
    if (top  + mH > window.innerHeight - 12) top  = rect.top - mH - gap;
    if (top  < 12) top  = 12;
    setDropdownPos({ top, left });
    setDropdownOpenId(id);
  };

  const openAddModal  = ()  => { setFormData(defaultForm); setActiveTab("details"); setModalType("add");  setDropdownOpenId(null); };
  const openViewModal = (p) => { if(!p) return; setSelectedItem(p); setModalType("view"); setDropdownOpenId(null); };
  const openEditModal = (p) => { if(!p) return; setFormData({ ...defaultForm, ...p }); setActiveTab("details"); setModalType("edit"); setDropdownOpenId(null); };
  const closeModal    = ()  => { setModalType(""); setSelectedItem(null); setFormData(defaultForm); };
  const openConfirm   = (id, action) => { setDropdownOpenId(null); setConfirmId(id); setConfirmAction(action); };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const method = modalType === "add" ? "POST" : "PUT";
      const url = modalType === "add" ? bElEdProgrammeApi : `${bElEdProgrammeApi}/${formData._id}`;
      const response = await apiClient(url, { method, body: JSON.stringify(formData) });
      if (response.ok) { fetchProgrammes(); closeModal(); }
    } catch (error) {
      console.error("Error saving programme:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePublish = async (id) => {
    try {
      setLoading(true);
      const item = programmes.find(p => p._id === id);
      const newStatus = item.Status === "Published" ? "Draft" : "Published";
      await apiClient(`${bElEdProgrammeApi}/${id}`, { method: "PUT", body: JSON.stringify({ ...item, Status: newStatus }) });
      fetchProgrammes(); setConfirmId(null); setConfirmAction("");
    } catch (error) {
      console.error("Error toggling status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await apiClient(`${bElEdProgrammeApi}/${id}`, { method: "DELETE" });
      fetchProgrammes(); setConfirmId(null); setConfirmAction("");
    } catch (error) {
      console.error("Error deleting programme:", error);
    } finally {
      setLoading(false);
    }
  };

  const publishedCount = programmes.filter((p) => p.Status === "Published").length;
  const draftCount     = programmes.filter((p) => p.Status === "Draft").length;
  const totalSeats     = programmes.reduce((s, p) => s + Number(p.Total_Seats || 0), 0);
  const currentDDItem  = programmes.find((p) => p._id === dropdownOpenId);

  return (
    <div className="beled-page">

      {/* ── Header ── */}
      <div className="beled-header">
        <div className="beled-header-left">
          <div className="beled-header-icon">🏫</div>
          <div>
            <div className="beled-breadcrumb">
              <span>Academics</span>
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
              <span>Admissions</span>
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
              <span className="beled-breadcrumb-active">B.El.Ed Programme</span>
            </div>
            <h1>B.El.Ed Programme</h1>
            <p>Manage Bachelor of Elementary Education programmes — add, edit, publish, and control visibility on the college website.</p>
          </div>
        </div>
        <button type="button" className="beled-add-btn" onClick={openAddModal}>
          <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Add Programme
        </button>
      </div>

      {/* ── Stats ── */}
      <div className="beled-stats">
        {[
          { cls: "total",     icon: "🏫", label: "Total Programmes", val: programmes.length },
          { cls: "published", icon: "🌐", label: "Published",        val: publishedCount },
          { cls: "draft",     icon: "📝", label: "Drafts",           val: draftCount },
          { cls: "seats",     icon: "🪑", label: "Total Seats",      val: totalSeats },
        ].map((s) => (
          <div key={s.cls} className={`beled-stat-card ${s.cls}`}>
            <div className="stat-icon-box">{s.icon}</div>
            <div className="stat-content">
              <span>{s.label}</span>
              <strong>{s.val}</strong>
            </div>
          </div>
        ))}
      </div>

      {/* ── Filters ── */}
      <div className="beled-filter-card">
        <div className="beled-filter-grid">
          <div className="fg">
            <label>Search</label>
            <div className="search-wrap">
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                type="text"
                placeholder="Name, code or specialization…"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              {searchText && (
                <button type="button" className="clear-search" onClick={() => setSearchText("")}>×</button>
              )}
            </div>
          </div>
          <div className="fg">
            <label>Specialization</label>
            <select value={filterSpec} onChange={(e) => setFilterSpec(e.target.value)}>
              <option value="all">All Specializations</option>
              {SPECIALIZATIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="fg">
            <label>Duration</label>
            <select value={filterDuration} onChange={(e) => setFilterDuration(e.target.value)}>
              <option value="all">All Durations</option>
              {DURATIONS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="fg">
            <label>Status</label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="all">All</option>
              <option value="Published">Published</option>
              <option value="Draft">Draft</option>
            </select>
          </div>
        </div>
        <div className="beled-filter-meta">
          Showing <strong>{filtered.length}</strong> of <strong>{programmes.length}</strong> programmes
          {(filterSpec !== "all" || filterStatus !== "all" || filterDuration !== "all" || searchText) && (
            <button
              type="button"
              className="clear-filters-btn"
              onClick={() => { setFilterSpec("all"); setFilterStatus("all"); setFilterDuration("all"); setSearchText(""); }}
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* ── Cards ── */}
      {filtered.length > 0 ? (
        <div className="beled-cards-grid">
          {filtered.map((prog) => {
            const color = getSpecColor(prog.specialization);
            const highlights = prog.highlights
              ? prog.highlights.split(",").map((h) => h.trim()).filter(Boolean)
              : [];
            return (
              <div key={prog._id} className={`beled-card ${prog.Status}`}>
                <div className="beled-card-bar" style={{ background: `linear-gradient(90deg, ${color}, ${color}88)` }} />

                <div className="beled-card-head">
                  <div className="beled-spec-badge" style={{ background: color + "18", color }}>
                    {prog.Specialization_Area || "B.El.Ed"}
                  </div>
                  <div className="beled-card-actions">
                    <span className={`beled-status-dot ${prog.Status}`} />
                    <button type="button" className="dots-btn" onClick={(e) => handleDotsClick(e, prog._id)}>⋮</button>
                  </div>
                </div>

                <div className="beled-card-body">
                  <div className="beled-card-code-row">
                    <span className="beled-card-code">{prog.Programme_Code}</span>
                    {prog.Accreditation_Approval && (
                      <span className="beled-accred-tag">{prog.Accreditation_Approval}</span>
                    )}
                  </div>
                  <h3 className="beled-card-title" onClick={() => openViewModal(prog)}>{prog.Programme_Name}</h3>
                  <p className="beled-card-desc">{prog.Programme_Description}</p>
                </div>

                <div className="beled-card-meta">
                  <div className="meta-chip">
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                    {prog.Duration}
                  </div>
                  <div className="meta-chip">
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                    {prog.Total_Seats} Seats
                  </div>
                  <div className="meta-chip">
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                    {prog.Annual_Fees}/yr
                  </div>
                  <div className="meta-chip">
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
                    {prog.Total_Semesters} Semesters
                  </div>
                </div>

                {prog.Practicum_Internship_Details && (
                  <div className="beled-practicum-strip" style={{ borderLeft: `3px solid ${color}` }}>
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke={color} strokeWidth="2"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>
                    <span>{prog.Practicum_Internship_Details}</span>
                  </div>
                )}

                {highlights.length > 0 && (
                  <div className="beled-card-highlights">
                    {highlights.slice(0, 3).map((h, i) => (
                      <span key={i} className="hl-chip" style={{ background: color + "12", color, borderColor: color + "25" }}>{h}</span>
                    ))}
                    {highlights.length > 3 && <span className="hl-chip more">+{highlights.length - 3}</span>}
                  </div>
                )}

                <div className="beled-card-footer">
                  <span className={`beled-status-badge ${prog.Status}`}>
                    {prog.Status === "Published" ? "● Published" : "● Draft"}
                  </span>
                  <div className="beled-card-btns">
                    <button type="button" className="card-btn-view" onClick={() => openViewModal(prog)}>View</button>
                    <button type="button" className="card-btn-edit" onClick={() => openEditModal(prog)}>Edit</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="beled-empty">
          <div className="empty-icon">🏫</div>
          <h3>No programmes found</h3>
          <p>
            {searchText || filterSpec !== "all" || filterStatus !== "all" || filterDuration !== "all"
              ? "Try adjusting your filters or search term."
              : "No B.El.Ed programmes added yet. Click \"Add Programme\" to get started."}
          </p>
          {!searchText && filterSpec === "all" && filterStatus === "all" && filterDuration === "all" && (
            <button type="button" className="beled-add-btn" onClick={openAddModal}>
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Add Programme
            </button>
          )}
        </div>
      )}

      {/* ── Three-dot Dropdown ── */}
      {dropdownOpenId !== null && (
        <div
          ref={dropdownRef}
          className="action-dropdown"
          style={{ position: "fixed", top: `${dropdownPos.top}px`, left: `${dropdownPos.left}px`, zIndex: 99999 }}
        >
          <button type="button" onClick={() => openViewModal(programmes.find((p) => p._id === dropdownOpenId))}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z"/><circle cx="12" cy="12" r="3"/></svg>
            View Details
          </button>
          <button type="button" onClick={() => openEditModal(programmes.find((p) => p._id === dropdownOpenId))}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
            Edit Programme
          </button>
          {currentDDItem && (
            <button
              type="button"
              className={currentDDItem.Status === "Published" ? "deactivate" : "activate"}
              onClick={() => openConfirm(dropdownOpenId, currentDDItem.Status === "Published" ? "unpublish" : "publish")}
            >
              {currentDDItem.Status === "Published" ? (
                <><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="8" y1="12" x2="16" y2="12"/></svg>Unpublish</>
              ) : (
                <><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/></svg>Publish</>
              )}
            </button>
          )}
          <div className="dropdown-divider" />
          <button type="button" className="delete" onClick={() => openConfirm(dropdownOpenId, "delete")}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
            Delete
          </button>
        </div>
      )}

      {/* ── Confirm Modal ── */}
      {confirmId !== null && (
        <div className="modal-overlay" onClick={() => setConfirmId(null)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className={`confirm-icon-box ${confirmAction}`}>
              {confirmAction === "delete" ? (
                <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
              ) : (
                <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              )}
            </div>
            <h3>
              {confirmAction === "delete" ? "Delete Programme?" :
               confirmAction === "unpublish" ? "Unpublish Programme?" : "Publish Programme?"}
            </h3>
            <p>
              {confirmAction === "delete"
                ? "This programme will be permanently deleted and cannot be recovered."
                : confirmAction === "unpublish"
                ? "This programme will be hidden from the public college website."
                : "This programme will be visible on the public college website."}
            </p>
            <div className="confirm-actions">
              <button type="button" className="btn btn-light" onClick={() => setConfirmId(null)}>Cancel</button>
              <button
                type="button"
                className={`btn ${confirmAction === "delete" ? "btn-danger" : "btn-warning"}`}
                onClick={() => confirmAction === "delete" ? handleDelete(confirmId) : handleTogglePublish(confirmId)}
              >
                {confirmAction === "delete" ? "Yes, Delete" :
                 confirmAction === "unpublish" ? "Yes, Unpublish" : "Yes, Publish"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add / Edit Modal ── */}
      {(modalType === "add" || modalType === "edit") && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="beled-modal" onClick={(e) => e.stopPropagation()}>

            <div className="modal-header">
              <div className="modal-header-left">
                <div className="modal-header-icon">🏫</div>
                <div>
                  <h2>{modalType === "add" ? "Add B.El.Ed Programme" : "Edit B.El.Ed Programme"}</h2>
                  <span>{modalType === "add" ? "Create a new B.El.Ed programme" : `Editing: ${formData.name || "Programme"}`}</span>
                </div>
              </div>
              <button type="button" className="modal-close" onClick={closeModal}>×</button>
            </div>

            <div className="modal-tabs">
              {[
                { key: "details",     label: "Basic Details",  icon: "📋" },
                { key: "academic",    label: "Academic Info",   icon: "📚" },
                { key: "eligibility", label: "Eligibility",     icon: "✅" },
                { key: "settings",    label: "Settings",        icon: "⚙️" },
              ].map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={`modal-tab ${activeTab === tab.key ? "active" : ""}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  <span>{tab.icon}</span>{tab.label}
                </button>
              ))}
            </div>

            <form className="modal-form" onSubmit={handleSave}>

              {/* ── Tab 1: Basic Details ── */}
              {activeTab === "details" && (
                <div className="tab-content">
                  <div className="form-group full">
                    <label>Programme Name <span className="req">*</span></label>
                    <input type="text" name="Programme_Name" value={formData.Programme_Name} onChange={handleChange}
                      placeholder="e.g. B.El.Ed — Language & Literacy" required />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Programme Code <span className="req">*</span></label>
                      <input type="text" name="Programme_Code" value={formData.Programme_Code} onChange={handleChange}
                        placeholder="e.g. BELED-LL" required />
                    </div>
                    <div className="form-group">
                      <label>Specialization Area</label>
                      <select name="Specialization_Area" value={formData.Specialization_Area} onChange={handleChange}>
                        <option value="">Select specialization</option>
                        {SPECIALIZATIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="form-group full">
                    <label>Programme Description <span className="req">*</span></label>
                    <textarea name="Programme_Description" value={formData.Programme_Description} onChange={handleChange}
                      placeholder="Describe the programme scope, teaching focus, target grade levels, and student outcomes..."
                      rows="4" required />
                  </div>
                  <div className="form-group full">
                    <label>Programme Highlights</label>
                    <input type="text" name="Programme_Highlights" value={formData.Programme_Highlights} onChange={handleChange}
                      placeholder="e.g. School Internship, Language Lab, Child Psychology (comma-separated)" />
                    <span className="field-hint">Separate with commas — displayed as tags on the website.</span>
                  </div>
                  <div className="form-group full">
                    <label>Practicum / Internship Details</label>
                    <input type="text" name="Practicum_Internship_Details" value={formData.Practicum_Internship_Details} onChange={handleChange}
                      placeholder="e.g. 2 Semesters of School Internship" />
                    <span className="field-hint">Shown as a highlighted strip on the programme card.</span>
                  </div>
                </div>
              )}

              {/* ── Tab 2: Academic Info ── */}
              {activeTab === "academic" && (
                <div className="tab-content">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Duration <span className="req">*</span></label>
                      <select name="Duration" value={formData.Duration} onChange={handleChange} required>
                        <option value="">Select duration</option>
                        {DURATIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Total Semesters <span className="req">*</span></label>
                      <input type="number" name="Total_Semesters" value={formData.Total_Semesters} onChange={handleChange}
                        placeholder="e.g. 8" min="1" max="10" required />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Total Seats <span className="req">*</span></label>
                      <input type="number" name="Total_Seats" value={formData.Total_Seats} onChange={handleChange}
                        placeholder="e.g. 50" min="1" required />
                    </div>
                    <div className="form-group">
                      <label>Annual Fees</label>
                      <input type="text" name="Annual_Fees" value={formData.Annual_Fees} onChange={handleChange}
                        placeholder="e.g. ₹28,000" />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Mode of Study</label>
                      <select name="Mode_of_Study" value={formData.Mode_of_Study} onChange={handleChange}>
                        {MODES.map((m) => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Affiliation</label>
                      <input type="text" name="Affiliation" value={formData.Affiliation} onChange={handleChange}
                        placeholder="e.g. Karnataka State University" />
                    </div>
                  </div>
                  <div className="form-group full">
                    <label>Accreditation / Approval</label>
                    <input type="text" name="Accreditation_Approval" value={formData.Accreditation_Approval} onChange={handleChange}
                      placeholder="e.g. NCTE Approved, NAAC A+" />
                  </div>

                  <div className="form-section-label" style={{ marginTop: "24px", marginBottom: "12px", fontWeight: "600", color: "#334155" }}>Programme Subjects</div>
                  <div className="subject-list">
                    {formData.Programme_Subjects && formData.Programme_Subjects.map((sub, idx) => (
                      <div key={sub.id || idx} className="subject-item-card" style={{ background: "#f8fafc", padding: "16px", borderRadius: "8px", border: "1px solid #e2e8f0", marginBottom: "16px" }}>
                        <div className="sic-header" style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", fontWeight: "600", fontSize: "14px", color: "#475569" }}>
                          <span>Subject #{idx + 1}</span>
                          <button type="button" style={{ color: "#ef4444", background: "none", border: "none", cursor: "pointer", fontSize: "13px" }} onClick={() => setFormData({...formData, Programme_Subjects: formData.Programme_Subjects.filter((_, i) => i !== idx)})}>Remove</button>
                        </div>
                        <div className="form-row" style={{ display: "flex", gap: "16px" }}>
                          <div className="form-group" style={{ flex: 1 }}>
                            <label style={{ display: "block", marginBottom: "8px", fontSize: "13px", fontWeight: "500", color: "#64748b" }}>Subject Name</label>
                            <input 
                              type="text"
                              value={sub.Subject_Name || ""} 
                              onChange={e => { const s = [...(formData.Programme_Subjects || [])]; s[idx].Subject_Name = e.target.value; setFormData({...formData, Programme_Subjects: s}) }} 
                              placeholder="e.g. History"
                              style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #cbd5e1" }}
                            />
                          </div>
                          <div className="form-group" style={{ flex: 1 }}>
                            <label style={{ display: "block", marginBottom: "8px", fontSize: "13px", fontWeight: "500", color: "#64748b" }}>Total Seats</label>
                            <input 
                              type="number"
                              value={sub.Total_Seats || ""} 
                              onChange={e => { const s = [...(formData.Programme_Subjects || [])]; s[idx].Total_Seats = e.target.value; setFormData({...formData, Programme_Subjects: s}) }} 
                              placeholder="e.g. 60"
                              style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #cbd5e1" }}
                            />
                          </div>
                          <div className="form-group" style={{ flex: 1 }}>
                            <label style={{ display: "block", marginBottom: "8px", fontSize: "13px", fontWeight: "500", color: "#64748b" }}>Group</label>
                            <input 
                              type="text"
                              value={sub.Group || ""} 
                              onChange={e => { const s = [...(formData.Programme_Subjects || [])]; s[idx].Group = e.target.value; setFormData({...formData, Programme_Subjects: s}) }} 
                              placeholder="e.g. Core / Elective"
                              style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #cbd5e1" }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button type="button" className="add-sub-btn" style={{ padding: "8px 16px", background: "#f1f5f9", color: "#334155", border: "1px dashed #cbd5e1", borderRadius: "6px", cursor: "pointer", fontWeight: "500", fontSize: "14px", width: "100%", marginBottom: "20px" }} onClick={() => setFormData({...formData, Programme_Subjects: [...(formData.Programme_Subjects || []), mkProgrammeSubject()]})}>
                    + Add New Subject
                  </button>

                  {/* Live Preview */}
                  {(formData.Programme_Name || formData.Specialization_Area) && (
                    <div className="programme-preview">
                      <div className="preview-label">Live Preview Card</div>
                      <div className="preview-card" style={{ borderLeft: `4px solid ${getSpecColor(formData.Specialization_Area)}` }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                          <span className="preview-spec" style={{ color: getSpecColor(formData.Specialization_Area), background: getSpecColor(formData.Specialization_Area) + "18" }}>
                            {formData.Specialization_Area || "Specialization"}
                          </span>
                          <span className="preview-code">{formData.Programme_Code || "CODE"}</span>
                        </div>
                        {formData.Accreditation_Approval && (
                          <div className="preview-accred">{formData.Accreditation_Approval}</div>
                        )}
                        <div className="preview-name">{formData.Programme_Name || "Programme Name"}</div>
                        <div className="preview-meta-row">
                          {formData.Duration   && <span>⏱ {formData.Duration}</span>}
                          {formData.Total_Seats && <span>👥 {formData.Total_Seats} Seats</span>}
                          {formData.Annual_Fees && <span>💰 {formData.Annual_Fees}/yr</span>}
                        </div>
                        {formData.Practicum_Internship_Details && (
                          <div className="preview-practicum">🏫 {formData.Practicum_Internship_Details}</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── Tab 3: Eligibility ── */}
              {activeTab === "eligibility" && (
                <div className="tab-content">
                  <div className="info-box">
                    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#0369a1" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/>
                      <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    B.El.Ed is an integrated undergraduate programme. Eligibility is 10+2 level. Mention board, stream preference, and minimum percentage clearly.
                  </div>
                  <div className="form-group full">
                    <label>Minimum Qualification Required</label>
                    <input type="text" name="Minimum_Qualification_Required" value={formData.Minimum_Qualification_Required} onChange={handleChange}
                      placeholder="e.g. 10+2 (Any Stream) / 10+2 (Science)" />
                    <span className="field-hint">Short label shown on the card and website.</span>
                  </div>
                  <div className="form-group full">
                    <label>Full Eligibility Criteria <span className="req">*</span></label>
                    <textarea name="Full_Eligibility_Criteria" value={formData.Full_Eligibility_Criteria} onChange={handleChange}
                      placeholder="e.g. 10+2 from a recognized board with minimum 50% marks. Candidates from any stream are eligible..."
                      rows="5" required />
                  </div>
                  {formData.Minimum_Qualification_Required && (
                    <div className="eligibility-preview">
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#059669" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                      Requires: <strong>{formData.Minimum_Qualification_Required}</strong>
                      {formData.Full_Eligibility_Criteria && ` — ${formData.Full_Eligibility_Criteria.slice(0, 80)}${formData.Full_Eligibility_Criteria.length > 80 ? "..." : ""}`}
                    </div>
                  )}
                </div>
              )}

              {/* ── Tab 4: Settings ── */}
              {activeTab === "settings" && (
                <div className="tab-content">
                  <div className="form-group full">
                    <label>Publication Status</label>
                    <div className="status-selector">
                      <label className={`status-option ${formData.Status === "Published" ? "selected" : ""}`}>
                        <input type="radio" name="Status" value="Published" checked={formData.Status === "Published"} onChange={handleChange} />
                        <div className="status-option-content">
                          <div className="status-icon published">🌐</div>
                          <div>
                            <strong>Published</strong>
                            <span>Visible on the college public website</span>
                          </div>
                        </div>
                      </label>
                      <label className={`status-option ${formData.Status === "Draft" ? "selected" : ""}`}>
                        <input type="radio" name="Status" value="Draft" checked={formData.Status === "Draft"} onChange={handleChange} />
                        <div className="status-option-content">
                          <div className="status-icon draft">📝</div>
                          <div>
                            <strong>Draft</strong>
                            <span>Hidden from the public website</span>
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>
                  <div className="settings-note">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z"/><circle cx="12" cy="12" r="3"/>
                    </svg>
                    Published programmes instantly appear on <strong>Admissions → B.El.Ed Programme</strong> on the student-facing website.
                  </div>
                </div>
              )}

              <div className="modal-footer">
                <div className="mf-left">
                  {activeTab !== "details" && (
                    <button type="button" className="btn btn-light" onClick={() => setActiveTab(TABS[TABS.indexOf(activeTab) - 1])}>
                      ← Previous
                    </button>
                  )}
                </div>
                <div className="mf-right">
                  <button type="button" className="btn btn-light" onClick={closeModal}>Cancel</button>
                  {activeTab !== "settings" ? (
                    <button type="button" className="btn btn-primary" onClick={() => setActiveTab(TABS[TABS.indexOf(activeTab) + 1])}>
                      Next →
                    </button>
                  ) : (
                    <button type="submit" className="btn btn-primary">
                      {modalType === "add" ? "✓ Add Programme" : "✓ Update Programme"}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── View Modal ── */}
      {modalType === "view" && selectedItem && (() => {
        const color  = getSpecColor(selectedItem.Specialization_Area);
        const hlList = selectedItem.Programme_Highlights
          ? selectedItem.Programme_Highlights.split(",").map((h) => h.trim()).filter(Boolean)
          : [];
        return (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="beled-modal view-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-header-left">
                  <div className="modal-header-icon" style={{ background: color + "20", color }}>🏫</div>
                  <div>
                    <h2>{selectedItem.Programme_Name}</h2>
                    <span style={{ color }}>{selectedItem.Specialization_Area || "B.El.Ed Programme"}</span>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <span className={`beled-status-badge ${selectedItem.Status}`}>
                    {selectedItem.Status === "Published" ? "● Published" : "● Draft"}
                  </span>
                  <button type="button" className="modal-close" onClick={closeModal}>×</button>
                </div>
              </div>

              <div className="view-body">
                {/* Hero */}
                <div className="view-hero" style={{ borderLeft: `5px solid ${color}`, background: `linear-gradient(135deg, ${color}10, ${color}05)` }}>
                  <div className="view-hero-left">
                    <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
                      <span className="view-code-tag" style={{ color, background: color + "18" }}>{selectedItem.Programme_Code}</span>
                      {selectedItem.Accreditation_Approval && (
                        <span className="view-accred-tag">{selectedItem.Accreditation_Approval}</span>
                      )}
                    </div>
                    <p>{selectedItem.Programme_Description}</p>
                    {selectedItem.Minimum_Qualification_Required && (
                      <div className="view-qual-tag">
                        <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="#22c55e" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                        Min. Qualification: <strong>{selectedItem.Minimum_Qualification_Required}</strong>
                      </div>
                    )}
                    {selectedItem.Practicum_Internship_Details && (
                      <div className="view-practicum-tag" style={{ borderColor: color, color }}>
                        <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>
                        {selectedItem.Practicum_Internship_Details}
                      </div>
                    )}
                  </div>
                  <div className="view-hero-stats">
                    {[
                      { val: selectedItem.Duration,          label: "Duration"    },
                      { val: selectedItem.Total_Semesters,   label: "Semesters"   },
                      { val: selectedItem.Total_Seats,       label: "Seats"       },
                      { val: selectedItem.Annual_Fees || "—", label: "Annual Fees" },
                    ].map((s) => (
                      <div key={s.label} className="hero-stat">
                        <strong>{s.val}</strong>
                        <span>{s.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Info Grid */}
                <div className="view-section-label">Programme Information</div>
                <div className="view-info-grid">
                  {[
                    { label: "Mode",           val: selectedItem.Mode_of_Study },
                    { label: "Specialization", val: selectedItem.Specialization_Area || "—" },
                    { label: "Affiliation",    val: selectedItem.Affiliation    || "—" },
                    { label: "Accreditation",  val: selectedItem.Accreditation_Approval  || "—" },
                  ].map((i) => (
                    <div key={i.label} className="view-info-item">
                      <span>{i.label}</span>
                      <strong>{i.val}</strong>
                    </div>
                  ))}
                </div>

                {/* Eligibility */}
                {selectedItem.Full_Eligibility_Criteria && (
                  <>
                    <div className="view-section-label">Eligibility Criteria</div>
                    <div className="view-eligibility-box">
                      <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="#059669" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                      {selectedItem.Full_Eligibility_Criteria}
                    </div>
                  </>
                )}

                {/* Highlights */}
                {hlList.length > 0 && (
                  <>
                    <div className="view-section-label">Programme Highlights</div>
                    <div className="view-highlights">
                      {hlList.map((h, i) => (
                        <span key={i} className="highlight-tag" style={{ background: color + "14", color, border: `1px solid ${color}28` }}>
                          ✦ {h}
                        </span>
                      ))}
                    </div>
                  </>
                )}

                <div className="view-footer-actions">
                  <button type="button" className="btn btn-light" onClick={closeModal}>Close</button>
                  <button type="button" className="btn btn-primary" onClick={() => { closeModal(); openEditModal(selectedItem); }}>
                    Edit Programme
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

    </div>
  );
};

export default BElEdProgramme;